import { MolsonSnackbar, type MolsonSnackbarProps } from '@components/reusable'
import type { SxProps, Theme } from '@mui/material'
import type { SnackbarCloseReason } from '@mui/material/Snackbar'
import { useCallback, useEffect, useState } from 'react'

declare global {
  interface WindowEventMap {
    toast: CustomEvent<Pick<MolsonSnackbarProps, 'message' | 'severity'>>
  }
}

type SnackbarBarData = {
  open: boolean
  sx?: SxProps<Theme>
  title?: string
  autoHideDuration?: number
  closableToast?: boolean
} & Pick<MolsonSnackbarProps, 'message' | 'severity'>

export const Snackbar = () => {
  const [snackbarData, setSnackbarData] = useState<SnackbarBarData>({
    message: '',
    open: false,
    severity: 'success',
    autoHideDuration: 6000
  })

  const handleToastMessage = useCallback((e: CustomEvent) => {
    const { detail } = e
    const { title, message, severity, sx, autoHideDuration, closableToast } =
      detail

    setSnackbarData({
      title,
      message,
      open: true,
      severity,
      autoHideDuration,
      sx,
      closableToast
    })
  }, [])

  const handleClose = (
    _?: React.SyntheticEvent | Event,
    closingReason?: SnackbarCloseReason
  ) => {
    if (snackbarData.closableToast && closingReason === 'clickaway') {
      return
    }

    setSnackbarData({
      ...snackbarData,
      open: false
    })
  }

  useEffect(() => {
    globalThis.addEventListener('toast', handleToastMessage)

    return () => {
      globalThis.removeEventListener('toast', handleToastMessage)
    }
  }, [handleToastMessage])

  return (
    <MolsonSnackbar
      message={snackbarData.message}
      onClose={handleClose}
      open={snackbarData.open}
      severity={snackbarData.severity}
      sx={snackbarData.sx}
      autoHideDuration={snackbarData.autoHideDuration}
      closableToast={snackbarData.closableToast}
      title={snackbarData.title}
    />
  )
}
